import { MenuItem } from '@mui/material'
import { ContainerType, OrderResponseDto } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import { useTranslate } from '@tolgee/react'
import { ThreeDotMenu } from '@tom-ui/ui'
import { MRT_TableInstance } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import {
  exportToCsv,
  generateFileNameWithDate,
  IDataExportConfig,
} from 'modules/ui/src/theme/components/table/utils/xlsxUtils'
import { getCarrierIdentifier } from '../../tableConfigs/useNonNumericReleaseDetailsColumns'

type Props = {
  tableRef?: MRT_TableInstance<IOrderItem>
  referenceNumber: string
  shippingLineName: string
  canEdit?: boolean
  onEdit: () => void
}

export const ReleaseDetailsOptions = observer((props: Props) => {
  const { tableRef, referenceNumber, shippingLineName, canEdit = true, onEdit } = props
  const { t } = useTranslate()
  const { appViewStore } = usePlanningStore()

  const rows = tableRef?.getPrePaginationRowModel().rows || []

  const isExportDisabled = rows.length === 0

  const onClickExportRelease = async () => {
    const rows = tableRef
      ?.getPrePaginationRowModel()
      .rows.filter(row => row.original.data.containerType !== ContainerType.Unknown)
      .map(row => {
        const orderItem = row.original
        const carrierName = getCarrierIdentifier(orderItem) ?? '-'

        return {
          containerNumber: orderItem.data.containerNumber,
          status: orderItem.data.status,
          containerIsoCode: orderItem.data.containerIsoCode,
          containerLength: orderItem.data.containerLength,
          containerHeight: orderItem.data.containerHeight,
          containerType: orderItem.data.containerType,
          carrierName: carrierName,
        }
      })

    if (!rows) {
      appViewStore.setShowAlert('warning', 'error', t('noDataToExport', 'No data to export'))
      return
    }

    const workbookTitle = `${shippingLineName}-${referenceNumber}`
    const filename = generateFileNameWithDate(workbookTitle, 'csv')
    const config: IDataExportConfig<OrderResponseDto> = {
      containerNumber: { header: t('containerNumber', 'Container number') },
      status: { header: t('status', 'Status') },
      containerIsoCode: { header: t('isoCode', 'ISO code') },
      containerLength: { header: t('containerLength', 'Container length') },
      containerHeight: { header: t('containerHeight', 'Container height') },
      containerType: { header: t('containerType', 'Container type') },
      carrierName: { header: t('carrier', 'Carrier') },
    }

    exportToCsv(workbookTitle, rows, config, filename)
  }

  return (
    <ThreeDotMenu>
      <MenuItem
        disabled={!canEdit}
        onClick={onEdit}
        sx={{ whiteSpace: 'nowrap' }}
        data-cy='header-edit-release-option'
      >
        {t('edit', 'Edit order')}
      </MenuItem>
      <MenuItem
        disabled={isExportDisabled}
        onClick={onClickExportRelease}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {t('export', 'Export')}
      </MenuItem>
    </ThreeDotMenu>
  )
})
